import type {AccountUsage, DownloadUsage} from "./AccountApi";

/**
 * Usage rules for every allowance an account can exhaust: the sender's calendar-month downloads
 * (ui#309 / api#206) and, since ui#1667, its live active storage.
 *
 * Pure domain — imports no React (epic #26): the escalation thresholds are product policy, not view
 * state, so they live here and the hooks/views merely render the result.
 *
 * STORAGE JOINED THIS MODULE RATHER THAN GETTING ITS OWN. The account card puts a storage bar directly
 * above a downloads bar, so two ladders would be two chances for 80% to mean different things on one
 * card. There is one {@link downloadUsageLevel} and one {@link DOWNLOAD_USAGE_WARNING_RATIO}, and both
 * meters read them.
 */

/** Escalation shared by every metered allowance: quiet below 80%, warn from 80%, full at 100%. */
export type DownloadUsageLevel = "ok" | "warning" | "reached";

/** The owner-confirmed warning threshold: 80% of the monthly cap. */
export const DOWNLOAD_USAGE_WARNING_RATIO = 0.8;

export type DownloadUsageView = {
	used: number;
	limit: number;
	/** Meter fill, 0–100, floored+capped: the bar reads 100 only when the window truly is full. */
	percent: number;
	level: DownloadUsageLevel;
};

export function downloadUsageLevel(used: number, limit: number): DownloadUsageLevel {
	if (used >= limit) {
		return "reached";
	}
	return used >= limit * DOWNLOAD_USAGE_WARNING_RATIO ? "warning" : "ok";
}

/** Any allowance with a consumed figure and a cap; null cap means unlimited. */
export type UsageAllowance = {
	used: number;
	limit: number | null;
};

/** An allowance with no proportion to show: the used figure stands on its own, with no bar. */
export type UnlimitedAllowanceView = {
	kind: "unlimited";
	used: number;
};

/** An allowance with a finite cap: everything a bar and its pill need. */
export type MeteredAllowanceView = {
	kind: "metered";
} & DownloadUsageView;

export type UsageAllowanceView = UnlimitedAllowanceView | MeteredAllowanceView;

/**
 * Shapes one allowance for the account card, or null when there is nothing to show at all — no data
 * yet, or a nonsensical non-positive cap.
 *
 * Unlike {@link downloadUsageView}, an UNLIMITED cap is a value here rather than a null. The banner
 * could collapse the two because both meant "draw nothing"; this card cannot, because unlimited draws
 * the used figure plus an "Unlimited" label and no-data draws no meter whatsoever.
 */
export function usageAllowanceView(allowance: UsageAllowance | null): UsageAllowanceView | null {
	if (allowance === null) {
		return null;
	}
	if (allowance.limit === null) {
		return {kind: "unlimited", used: allowance.used};
	}
	const metered = meteredView(allowance.used, allowance.limit);
	return metered === null ? null : {kind: "metered", ...metered};
}

/**
 * Shapes the API usage into everything the sender's banner meter renders, or null when there is
 * nothing to meter: no data yet, an unlimited (null) cap, or a nonsensical non-positive cap.
 */
export function downloadUsageView(usage: DownloadUsage | null): DownloadUsageView | null {
	return usage === null || usage.limit === null ? null : meteredView(usage.used, usage.limit);
}

/** The metered arithmetic both views share; null for a cap that cannot be metered against. */
function meteredView(used: number, limit: number): DownloadUsageView | null {
	if (limit <= 0) {
		return null;
	}
	return {
		used,
		limit,
		// floor, not round: 199/200 must read 99 — a "nearing" title beside a 100% bar contradicts itself.
		percent: Math.min(100, Math.floor((used / limit) * 100)),
		level: downloadUsageLevel(used, limit),
	};
}

/** Both of the account card's meters, each shaped independently (ui#1667). */
export type AccountUsageView = {
	storage: UsageAllowanceView | null;
	downloads: UsageAllowanceView | null;
};

/**
 * Shapes the live `GetUsage` response into the card's two meters.
 *
 * This is where the api's asymmetric spelling is normalised: storage arrives as `usedBytes`/
 * `limitBytes` and downloads as `used`/`limit`, because the download pair is frozen by the deployed
 * clients of the older endpoint. Doing it here keeps that wire detail out of the card, and keeps the
 * caps coming from the RESPONSE — never from the client plan catalogue, which cannot know what a
 * referral-trial account is entitled to.
 */
export function accountUsageView(usage: Pick<AccountUsage, "storage" | "downloads"> | null): AccountUsageView {
	if (usage === null) {
		return {storage: null, downloads: null};
	}
	return {
		storage: usageAllowanceView({used: usage.storage.usedBytes, limit: usage.storage.limitBytes}),
		downloads: usageAllowanceView(usage.downloads),
	};
}

/** The banner shows only once the warning threshold is met — the meter stays quiet below it. */
export function shouldShowUsageBanner(view: DownloadUsageView | null): boolean {
	return view !== null && view.level !== "ok";
}
